// Online play: a read-only mirror of the server's game for this seat, plus the polling client.
import Majiang from '@kobalab/majiang-core';
import { SpeedGame } from '../game.js';

// Mirrors the server's view so the UI can treat it like a local SpeedGame.
// Seats are rotated so index 0 is always you.
export class OnlineGame {
  constructor(send) {
    this.send = send;
    this.online = true;
    this.onChange = () => {};
    this.riichiArmed = false;
    this.pending = false;
    this.version = -1;
    this.receivedAt = 0;
  }

  update(room) {
    if (!room.game) return;
    const g = room.game;
    if (room.version < this.version || (room.version === this.version && room.serverNow < this.serverNow)) return;
    const prevMe = this.players?.[0];
    const prevPhase = prevMe?.phase;
    const prevRound = this.round;
    const prevEvent = this.lastEvent?.id;
    this.version = room.version;
    this.serverNow = room.serverNow;
    this.receivedAt = performance.now();
    this.me = room.you;
    const rel = (abs) => (abs - this.me + 4) % 4;
    const order = Array.from({ length: 4 }, (_, i) => (i + this.me) % 4);
    this.players = order.map((abs, i) => {
      const p = g.players[abs];
      let hand;
      if (p.hand !== undefined) hand = Majiang.Shoupai.fromString(p.hand);
      else {
        hand = new Majiang.Shoupai();
        hand._fulou = [...p.melds];
        hand._lizhi = p.riichi;
        hand._zimo = p.holding ? '?' : null;
      }
      return { ...p, id: i, hand };
    });
    this.status = g.status;
    this.round = g.round;
    this.dealer = rel(g.dealer);
    this.scores = order.map((abs) => g.scores[abs]);
    this.pot = g.pot;
    this.wall = { length: g.wallCount };
    this.dead = [g.dora, g.ura];
    this.elapsed = g.elapsed;
    this.revision = g.revision;
    this.log = g.log;
    this.reservations = g.reservations;
    this.autoDraw = g.autoDraw;
    this.speed = g.cpuSpeed || room.cpuSpeed || 'normal';
    this.canSetSpeed = !!room.host;
    this.startAt = g.startAt;
    this.nextAt = g.nextAt;
    this._riichi = g.riichiDiscards;
    this._waits = g.waits;
    this._furiten = g.furiten;
    this._ranking = g.ranking.map(rel);
    this._clock = g.clock;
    this.lastDiscard = g.lastDiscard ? { ...g.lastDiscard, player: rel(g.lastDiscard.player) } : null;
    this.lastTimeout = g.lastTimeout ? { ...g.lastTimeout, player: rel(g.lastTimeout.player) } : null;
    const resultKey = g.result ? JSON.stringify([g.round, g.result]) : '';
    if (resultKey !== this.resultKey) {
      this.resultKey = resultKey;
      this.result = g.result
        ? {
            ...g.result,
            delta: order.map((abs) => g.result.delta[abs]),
            tenpai: g.result.tenpai ? order.map((abs) => g.result.tenpai[abs]) : undefined,
            winner: g.result.winner == null ? undefined : rel(g.result.winner),
            from: g.result.from == null ? null : rel(g.result.from),
            potAward: g.result.potAward ? { ...g.result.potAward, player: rel(g.result.potAward.player) } : undefined,
          }
        : null;
    }
    if (prevPhase !== this.players[0].phase || prevRound !== this.round || this.status !== 'playing') this.riichiArmed = false;
    this.lastEvent = g.lastEvent;
    let event = g.lastEvent?.id !== prevEvent ? g.lastEvent : { event: 'sync', message: '' };
    if (prevMe && prevRound === this.round && this.players[0].timeoutCount > prevMe.timeoutCount) {
      this.lastTimeout = { ...this.players[0].lastTimeout, player: 0 };
      event = {
        event: 'timeout',
        message: this.lastTimeout.action === 'draw' ? '5 seconds passed, so a tile was drawn for you.' : '5 seconds passed, so a tile was discarded for you.',
      };
    }
    this.onChange(event || { event: 'sync' });
  }

  seat(id) {
    return (id - this.dealer + 4) % 4;
  }
  waits() {
    return this._waits;
  }
  furiten() {
    return this._furiten;
  }
  riichiDiscards() {
    return this._riichi;
  }
  ranking() {
    return this._ranking;
  }
  notify(event = 'update', message = '') {
    this.onChange({ event, message });
  }
  actionClock() {
    if (!this._clock || this.status === 'ready') return null;
    return {
      ...this._clock,
      remainingMs: Math.max(0, this._clock.remainingMs - (this._clock.running ? performance.now() - this.receivedAt : 0)),
    };
  }
  now() {
    return this.serverNow + performance.now() - this.receivedAt;
  }

  async act(action, extra = {}) {
    if (this.pending) return false;
    this.pending = true;
    this.notify('pending');
    try {
      return await this.send({ action, round: this.round, phase: this.players[0].phase, ...extra });
    } finally {
      this.pending = false;
      this.notify('pending');
    }
  }
  draw() {
    return this.players[0].hand.lizhi ? false : this.act('draw');
  }
  discard(_id, tile) {
    return this.players[0].hand.lizhi ? false : this.act('discard', { tile, declare: this.riichiArmed });
  }
  armRiichi() {
    if (this.pending || this.status !== 'playing' || (!this.riichiArmed && !this._riichi.length)) return false;
    this.riichiArmed = !this.riichiArmed;
    this.notify();
    return true;
  }
  setAutoDraw(value) {
    return this.act('auto', { value });
  }
  setSpeed(speed) {
    if (!this.canSetSpeed) throw new Error('Only the host can change CPU speed.');
    return this.act('cpu-speed', { speed });
  }
  setReservations(keys) {
    return this.act('reservations', { keys });
  }
  toggleReservation(key) {
    return this.setReservations(this.reservations.filter((r) => r.key !== key).map((r) => r.key));
  }
  clearReservations() {
    return this.setReservations([]);
  }
  holdSelection(value) {
    return this.send({ action: 'hold', value, round: this.round });
  }
  tick() {}
  pause() {
    return false;
  }
  resume() {
    return false;
  }
  snapshot() {
    return { ...SpeedGame.prototype.snapshot.call(this), online: true, pending: this.pending, cpuSpeed: this.speed, canSetCpuSpeed: this.canSetSpeed };
  }
}

// Talks to the server: JSON requests plus adaptive polling of /api/room.
export class OnlineClient {
  constructor({ onRoom, onStatus }) {
    this.onRoom = onRoom;
    this.onStatus = onStatus;
    this.room = null;
    this.timer = null;
    this.polling = false;
    this.pollStarted = 0;
    this.pollFailures = 0;
    this.enabled = false;
    this.lastGood = 0;
    this.leftRooms = new Set();
    this.actionTail = Promise.resolve();
    this.serial = 0;
    this.appliedTicket = 0;
    this.initializing = null;
  }

  async request(path, body) {
    const ticket = ++this.serial;
    let res;
    try {
      res = await fetch(path, {
        method: body === undefined ? 'GET' : 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: body === undefined ? {} : { 'content-type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: AbortSignal.timeout(10000),
      });
    } catch (cause) {
      const err = new Error('Connection lost. Waiting to reconnect…', { cause });
      err.name = cause.name;
      throw err;
    }
    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error("Can't reach the server. Wait a moment and reconnect.");
    }
    if (data.room !== undefined && ((data.room && data.room.id === this.room?.id) || ticket >= this.appliedTicket)) {
      this.accept(data.room);
      this.appliedTicket = Math.max(this.appliedTicket, ticket);
    }
    if (!res.ok) throw new Error(data.error || 'Could not connect.');
    this.lastGood = performance.now();
    this.onStatus('connected');
    return data;
  }

  accept(room) {
    if (room && this.leftRooms.has(room.id)) return;
    if (room && this.room?.id === room.id && (room.version < this.room.version || (room.version === this.room.version && room.serverNow < this.room.serverNow))) return;
    this.room = room;
    this.roomReceivedAt = performance.now();
    this.onRoom(room);
  }

  async init(name) {
    if (this.initializing) {
      const r = await this.initializing;
      if (name === undefined) return r;
    }
    const p = this.request('/api/session', name === undefined ? {} : { name });
    this.initializing = p;
    try {
      const r = await p;
      this.enabled = true;
      this.schedule();
      return r;
    } finally {
      if (this.initializing === p) this.initializing = null;
    }
  }

  async lobby(kind, name, code) {
    await this.init(name);
    // Rooms keep their id, so forget that we left one or rejoining it would be ignored.
    this.leftRooms.clear();
    return this.request('/api/rooms/' + kind, { code });
  }

  action(body) {
    const p = this.actionTail.then(() => this._action(body));
    this.actionTail = p.catch(() => {});
    return p;
  }

  async _action(body) {
    const payload = { ...body, requestId: crypto.randomUUID() };
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        return await this.request('/api/rooms/action', payload);
      } catch (e) {
        if (attempt === 0 && (e.name === 'TypeError' || e.name === 'TimeoutError')) continue;
        throw e;
      }
    }
  }

  async leave() {
    const id = this.room?.id;
    const r = await this.request('/api/rooms/leave', {});
    if (id) this.leftRooms.add(id);
    this.schedule();
    return r;
  }

  schedule() {
    if (!this.enabled || !this.room) {
      clearTimeout(this.timer);
      this.timer = null;
      return;
    }
    if (this.polling) return;
    const gap = this.pollFailures ? Math.min(4000, 1000 * 2 ** (this.pollFailures - 1)) : this.room.game?.status === 'playing' ? 250 : 1000;
    const at = Math.max(performance.now() + 50, this.pollStarted + gap);
    if (this.timer && this.pollAt <= at) return;
    clearTimeout(this.timer);
    this.pollAt = at;
    this.timer = setTimeout(() => this.poll(), at - performance.now());
  }

  async poll() {
    if (this.polling) return;
    clearTimeout(this.timer);
    this.timer = null;
    this.polling = true;
    this.pollStarted = performance.now();
    try {
      await this.request('/api/room');
      this.pollFailures = 0;
    } catch {
      this.pollFailures++;
      this.onStatus('reconnecting');
    } finally {
      this.polling = false;
      this.schedule();
    }
  }

  retry() {
    return this.enabled ? this.poll() : this.init();
  }
}
